import { useMemo } from 'react';

import { ScanEyeIcon } from 'lucide-react';
import * as Accordion from '@radix-ui/react-accordion';
import clsx from 'clsx';

import { convertToFourDecimalPlaces } from '@/utils/numbers';
import { CaseImageData } from '@/api/cases';

import { FetchStates } from '@/components/fetch-states';

type Segmentation = 'vessels' | 'optic_disc' | 'lesions';
type LayerImage = { layer: string; url: string };

type RightSideDataProps = {
  img: string;
  segmentationSelected?: Segmentation[];
  setSegmentationSelected: (segmentation?: Segmentation[]) => void;
  layerImages?: { vessels?: LayerImage; optic_disc?: LayerImage; lesions?: LayerImage };
  segmentationData?: CaseImageData[];
  loading: boolean;
};

export function ImageSegDataAccordionSelect(props: RightSideDataProps) {
  const {
    segmentationSelected = [],
    setSegmentationSelected,
    img,
    layerImages,
    segmentationData,
    loading,
  } = props;

  const expandedImageData = useMemo(() => {
    if (!segmentationData || !img) return undefined;
    return segmentationData.find((data) => img.includes(data.image_filename));
  }, [segmentationData, img]);

  const isVasselsEnabled =
    Boolean(expandedImageData?.vessel_segment_vals) || Boolean(layerImages?.vessels);
  const isOpticDiscEnabled =
    Boolean(expandedImageData?.optic_disc_segment_vals) || Boolean(layerImages?.optic_disc);
  const isLesionsEnabled =
    Boolean(expandedImageData?.lesion_segment_vals) || Boolean(layerImages?.lesions);

  if (!isVasselsEnabled && !isOpticDiscEnabled && !isLesionsEnabled) {
    return (
      <div className="rounded-lg bg-white bg-opacity-90 p-4 inline-flex flex-col shrink-0 w-full lg:w-[48%] xl:w-52 order-2 xl:order-3">
        <FetchStates loading={loading}>
          <span className="text-gray-500">No insights data available for this image</span>
        </FetchStates>
      </div>
    );
  }

  return (
    <Accordion.Root
      className="rounded-lg bg-white bg-opacity-90 p-4 inline-flex flex-col shrink-0 w-full lg:w-[48%] xl:w-52 order-2 xl:order-3"
      type="multiple"
      value={segmentationSelected}
      onValueChange={(value) => setSegmentationSelected(value as Segmentation[] | undefined)}
    >
      <FetchStates loading={loading}>
        {isVasselsEnabled ? (
          <>
            <Accordion.Item value="vessels">
              <Accordion.Trigger className="w-full pb-2 flex justify-between items-end cursor-pointer">
                <span>Vessels</span>
                <ScanEyeIcon
                  className={clsx('h-8 w-8 -mr-2 p-2 rounded-full', {
                    'text-gray-500': !segmentationSelected?.includes('vessels'),
                    'text-white bg-primary': segmentationSelected?.includes('vessels'),
                  })}
                />
              </Accordion.Trigger>
              <div className="overflow-hidden pb-4">
                <Accordion.Content className="text-gray-600 text-sm">
                  {expandedImageData?.vessel_segment_vals ? (
                    <>
                      <span>
                        Tortuosity:{' '}
                        {convertToFourDecimalPlaces(
                          expandedImageData?.vessel_segment_vals.Tortuosity
                        )}
                      </span>
                      <br />
                      <span>
                        Fractals:{' '}
                        {convertToFourDecimalPlaces(
                          expandedImageData?.vessel_segment_vals.Fractals
                        )}
                      </span>
                      <br />
                      <span>
                        A/V ratio:{' '}
                        {convertToFourDecimalPlaces(
                          expandedImageData?.vessel_segment_vals['A/V Ratio']
                        )}
                      </span>
                      <br />
                      <span>
                        Density:{' '}
                        {convertToFourDecimalPlaces(
                          expandedImageData?.vessel_segment_vals['Vessel Density']
                        )}
                      </span>
                    </>
                  ) : null}
                </Accordion.Content>
              </div>
            </Accordion.Item>
            <hr className="mb-4" />
          </>
        ) : null}
        {isOpticDiscEnabled ? (
          <>
            <Accordion.Item value="optic_disc">
              <Accordion.Trigger className="w-full pb-2 flex justify-between items-end cursor-pointer">
                <span>Optic Disc</span>
                <ScanEyeIcon
                  className={clsx('h-8 w-8 -mr-2 p-2 rounded-full', {
                    'text-gray-500': !segmentationSelected?.includes('optic_disc'),
                    'text-white bg-primary': segmentationSelected?.includes('optic_disc'),
                  })}
                />
              </Accordion.Trigger>
              <div className="overflow-hidden pb-4">
                <Accordion.Content className="text-gray-600 text-sm">
                  {expandedImageData?.optic_disc_segment_vals ? (
                    <>
                      <span>
                        C/D Ratio:{' '}
                        {convertToFourDecimalPlaces(
                          expandedImageData.optic_disc_segment_vals?.Cup_to_Disc_Ratio
                        )}
                      </span>
                      <br />
                      <span>
                        OD Diam Ratio:{' '}
                        {convertToFourDecimalPlaces(
                          expandedImageData.optic_disc_segment_vals?.OD_Diam_Ratio
                        )}
                      </span>
                    </>
                  ) : null}
                </Accordion.Content>
              </div>
            </Accordion.Item>
            <hr className="mb-4" />
          </>
        ) : null}
        {isLesionsEnabled ? (
          <Accordion.Item value="lesions">
            <Accordion.Trigger className="w-full pb-2 flex justify-between items-end cursor-pointer">
              <span>Lesions</span>
              <ScanEyeIcon
                className={clsx('h-8 w-8 -mr-2 p-2 rounded-full', {
                  'text-gray-500': !segmentationSelected?.includes('lesions'),
                  'text-white bg-primary': segmentationSelected?.includes('lesions'),
                })}
              />
            </Accordion.Trigger>
            <div className="overflow-hidden pb-4">
              <Accordion.Content className="text-gray-600 text-sm">
                {expandedImageData?.lesion_segment_vals ? (
                  <>
                    <span>
                      Red Lesions: {parseInt(expandedImageData.lesion_segment_vals['Red Lesions'])}
                    </span>
                    <br />
                    <span>
                      Bright Lesions:{' '}
                      {parseInt(expandedImageData.lesion_segment_vals['Bright Lesions'])}
                    </span>
                    <br />
                    <span>
                      Near Fovea:{' '}
                      {expandedImageData.lesion_segment_vals['Near Fovea'] === 'true'
                        ? 'Yes'
                        : 'No'}
                    </span>
                    <br />
                    <span>
                      Area: {convertToFourDecimalPlaces(expandedImageData.lesion_segment_vals.Area)}
                    </span>
                    <br />
                    <span>
                      Quadrant: {parseInt(expandedImageData.lesion_segment_vals.Quadrants)}
                    </span>
                  </>
                ) : null}
              </Accordion.Content>
            </div>
          </Accordion.Item>
        ) : null}
      </FetchStates>
    </Accordion.Root>
  );
}
